import { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { useDispatch } from "react-redux";
import { useAuth } from "../context/AuthContext";
import { initializeAxiosInterceptor } from "../services/api/axiosInterceptor";
import { postDataFromApi } from "../services/api/apiService";
import { RestfulUrls } from "../constants/restfulUrls";
import { actionTypes } from "../store/actionTypes/actionTypes";
import { COMMON } from "../constants/common";
import { RouterPathEnum } from "../constants/router";
import { IApiResponse } from "../interfaces/common/IApiResponse";

/**
 * Component that sets up the axios interceptor for token refresh
 * This should be rendered once at the app level
 * Also fetches user details on page refresh
 */
const AxiosInterceptorSetup: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { instance } = useMsal();
  const { updateAccessToken, updateUserRole } = useAuth();
  const hasFetchedUserDetails = useRef(false);

  useEffect(() => {
    // Initialize the axios interceptor with required dependencies
    initializeAxiosInterceptor(instance, navigate, updateAccessToken);
  }, [instance, navigate, updateAccessToken]);

  // Fetch user details on page refresh (mount)
  useEffect(() => {
    // Skip fetching user details on login page or if already fetched
    if (
      location.pathname.includes(RouterPathEnum.LOGIN) ||
      location.pathname.includes(RouterPathEnum.USER_REQUEST) ||
      location.pathname.includes(RouterPathEnum.USER_INACTIVE) ||
      location.pathname.includes(RouterPathEnum.UNAUTHORIZED) ||
      hasFetchedUserDetails.current
    ) {
      return;
    }

    // Check if user is authenticated (has token)
    const token = localStorage.getItem(COMMON.ACCESS_TOKEN);
    if (!token) {
      return; // No token, user needs to login
    }

    const fetchUserDetails = async () => {
      hasFetchedUserDetails.current = true;

      try {
        const url = `${RestfulUrls.BASE_URL}${RestfulUrls.GET_USER_DETAILS}`;
        const payloadData = null;

        postDataFromApi<any>(
          url,
          payloadData,
          (response: IApiResponse<any>) => {
            // Update user role in context
            if (response.data?.userDetails?.roleName) {
              updateUserRole(response.data.userDetails.roleName);
            }

            // Update Redux store
            dispatch({
              type: actionTypes.UPDATE_USER_DETAILS,
              payload: response?.data?.userDetails,
            });
            dispatch({
              type: actionTypes.UPDATE_USER_PERMISSION,
              payload: response?.data?.role_permission,
            });

            // Update localStorage
            if (response.data?.userDetails) {
              localStorage.setItem(
                COMMON.USER_DETAILS_LS_KEY.DETAILS,
                JSON.stringify(response.data.userDetails)
              );
            }
            if (response.data?.role_permission) {
              localStorage.setItem(
                COMMON.USER_DETAILS_LS_KEY.ROLE_PERMISSION,
                JSON.stringify(response.data.role_permission)
              );
            }
          },
          { isAuthRequired: true },
          navigate
          // Error handling (including UserDoesNotExists and UserInactive) is handled by apiService
        );
      } catch (error) {
        console.error("Error fetching user details on page refresh:", error);
        // Error is handled by apiService, which may redirect to appropriate pages
      }
    };

    fetchUserDetails();
  }, [location.pathname, dispatch, navigate, updateUserRole]);

  return null; // This component doesn't render anything
};

export default AxiosInterceptorSetup;
